/**
 * useUserConnection — the single per-tab WebSocket that powers every
 * real-time UI behaviour.
 *
 * Pattern (per docs/frontend-patterns.md):
 *   - The server sends events; the client invalidates queries.
 *   - We never patch local cache from event payloads. Invalidate-then-
 *     refetch guarantees convergence to server truth.
 *
 * The hook is mounted exactly once, from AppShell, while the user is
 * authenticated. It opens one WebSocket against the Worker's
 * `/ws/user` endpoint; the UserChannel Durable Object on the other
 * side fans out events from every household this user belongs to
 * (see apps/api/src/user_channel.ts).
 *
 * Reliability features:
 *   - Sequence-based replay: every event carries a monotonically
 *     increasing `seq`. On reconnect the client passes its last-seen
 *     id in the query string, and the server replays whatever's still
 *     in its in-memory ring buffer. If the gap exceeds the buffer it
 *     sends a `missed` message and the client wide-invalidates.
 *   - Exponential backoff: 1s → 2s → 5s → 15s → 30s capped, reset on
 *     successful open.
 *   - Tab visibility / focus: when the tab comes back from background
 *     we short-circuit the backoff timer and reconnect immediately,
 *     plus invalidate as a safety net. Most "stale UI after sleep"
 *     reports are fixed by this.
 *   - Online/offline: `navigator.onLine` transitions are first-class
 *     status changes so the connection-status toast can react.
 *   - Server-driven disconnect (code 4001): the server has revoked the
 *     session; we stay disconnected and let the auth layer redirect.
 *
 * Out of scope for this hook:
 *   - Auth re-establishment (the `trpc.auth.me` query already drives
 *     login redirects when the cookie expires).
 *   - Local event handling beyond invalidation (see the `event` case).
 */

import type { GroupEventEmission, GroupEventType } from "@fellesen/api/events";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { wsUrl } from "./trpc";

export type ConnectionStatus = "connecting" | "connected" | "reconnecting" | "offline";

interface ConnectedMessage {
  type: "connected";
  seq: number;
}
interface EventMessage {
  type: "event";
  seq: number;
  event: GroupEventEmission;
}
interface MissedMessage {
  type: "missed";
}
interface PongMessage {
  type: "pong";
}
type ServerMessage = ConnectedMessage | EventMessage | MissedMessage | PongMessage;

/**
 * Close code the server uses when revoking an active session (logout,
 * password change, "sign out all sessions"). Mirrors
 * `CLOSE_CODE_SESSION_ENDED` in apps/api/src/user_channel.ts.
 */
const CLOSE_CODE_SESSION_ENDED = 4001;

/** Backoff schedule (ms) for reconnect attempts. Last entry is the cap. */
const RECONNECT_DELAYS_MS = [1_000, 2_000, 5_000, 15_000, 30_000];

export interface UseUserConnectionOptions {
  /**
   * Set false while auth is still loading or the user is logged out.
   * The hook will not open a socket until this flips true. Flipping
   * back to false closes the active connection.
   */
  enabled?: boolean;
}

export function useUserConnection(options?: UseUserConnectionOptions): {
  status: ConnectionStatus;
} {
  const enabled = options?.enabled ?? true;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [status, setStatus] = useState<ConnectionStatus>(enabled ? "connecting" : "offline");

  // Persist across the effect's lifecycle without causing re-renders.
  const wsRef = useRef<WebSocket | null>(null);
  const lastSeenSeqRef = useRef<number | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const attemptRef = useRef(0);
  const sessionEndedRef = useRef(false);

  useEffect(() => {
    /**
     * Detach handlers BEFORE closing so the close doesn't bounce back
     * into our state machine as a "we lost connection" signal. Used
     * everywhere we close intentionally (cleanup, manual reconnect,
     * online/offline transitions). Without this, React Strict Mode's
     * dev-time double-effect would fire the cleanup close → `onclose`
     * → status="reconnecting", then the remount → status="connected",
     * which the connection-status toast reads as a recovery and shows
     * "Tilkoblet igjen" on every fresh page load.
     */
    const closeSocket = (ws: WebSocket | null) => {
      if (!ws) return;
      ws.onopen = null;
      ws.onmessage = null;
      ws.onclose = null;
      ws.onerror = null;
      try {
        ws.close();
      } catch {
        // already closed
      }
    };

    if (!enabled) {
      // Tear down any prior connection — the auth state changed.
      closeSocket(wsRef.current);
      wsRef.current = null;
      setStatus("offline");
      return;
    }

    sessionEndedRef.current = false;

    /**
     * Wide invalidation predicate — same shape as the old SSE hook.
     * Matches every TanStack Query whose top-level key starts with
     * one of the group-scoped tRPC paths. The frontend uses tRPC v11
     * key format `[["settlement","list"], { input }]`, so we look at
     * `queryKey[0][0]`.
     */
    const invalidate = () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const root = query.queryKey[0];
          if (!Array.isArray(root)) return false;
          const head = root[0];
          return head === "settlement" || head === "household" || head === "invitation";
        },
      });
    };

    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const scheduleReconnect = () => {
      if (sessionEndedRef.current) return;
      if (reconnectTimerRef.current !== null) return;
      const attempt = attemptRef.current + 1;
      attemptRef.current = attempt;
      const delay = RECONNECT_DELAYS_MS[Math.min(attempt - 1, RECONNECT_DELAYS_MS.length - 1)];
      console.warn("[realtime] reconnecting", { attempt, delayMs: delay });
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        connect();
      }, delay);
    };

    const handleEvent = (eventType: GroupEventType, event: GroupEventEmission) => {
      // permissions.revoked is the only event the hook reacts to beyond
      // a generic invalidate. The server sends it to a user who just
      // lost access to a household; we route them off the household
      // route so the UI doesn't sit on data they can no longer fetch.
      if (eventType === "permissions.revoked") {
        invalidate();
        const path = window.location.pathname;
        if (path.includes(`/households/${event.groupId}`)) {
          navigate({ to: "/" }).catch(() => {
            // Router navigation can fail when the destination route
            // hasn't loaded; fall back to a hard navigation.
            window.location.assign("/");
          });
        }
        return;
      }
      invalidate();
    };

    const connect = () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
      // Make sure no zombie socket lingers, and detach its handlers so
      // the close doesn't fire our reconnect logic for a socket we are
      // intentionally replacing.
      closeSocket(wsRef.current);

      const seq = lastSeenSeqRef.current;
      const path = seq !== null ? `/ws/user?lastSeenSeq=${seq}` : "/ws/user";
      let ws: WebSocket;
      try {
        ws = new WebSocket(wsUrl(path));
      } catch (err) {
        // SecurityError (mixed content) or SYNTAX_ERR — fail to
        // reconnect-mode and let the backoff retry, in case the
        // misconfiguration was transient.
        console.warn("[realtime] WebSocket construction failed", { err });
        setStatus("reconnecting");
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        attemptRef.current = 0;
        // `connected` payload (seq snapshot) will arrive next. Until
        // then keep status as `connecting` so the toast doesn't flash
        // green prematurely. Actually, opening means the handshake
        // succeeded — flip status now for snappy UX.
        setStatus("connected");
      };

      ws.onmessage = (e) => {
        let msg: ServerMessage;
        try {
          msg = JSON.parse(typeof e.data === "string" ? e.data : "") as ServerMessage;
        } catch {
          return;
        }
        switch (msg.type) {
          case "connected":
            lastSeenSeqRef.current = msg.seq;
            // Safety-net invalidate: we may have missed events while
            // the socket was down even when the server didn't send
            // `missed` (the buffer covered our gap). Cheap because
            // TanStack Query dedupes refetches.
            invalidate();
            setStatus("connected");
            break;
          case "event":
            lastSeenSeqRef.current = msg.seq;
            handleEvent(msg.event.eventType, msg.event);
            break;
          case "missed":
            invalidate();
            break;
          case "pong":
            // No-op — emitted in response to client pings if we ever
            // add them. Currently unused.
            break;
        }
      };

      ws.onclose = (e) => {
        if (e.code === CLOSE_CODE_SESSION_ENDED) {
          sessionEndedRef.current = true;
          setStatus("offline");
          return;
        }
        if (typeof navigator !== "undefined" && navigator.onLine === false) {
          setStatus("offline");
          return;
        }
        setStatus("reconnecting");
        scheduleReconnect();
      };

      ws.onerror = () => {
        // Errors are followed by a close event; let onclose drive the
        // reconnect decision so we don't double-schedule.
      };
    };

    const handleVisibilityOrFocus = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      if (sessionEndedRef.current) return;
      // Wide invalidate unconditionally — covers cases where the
      // socket *thinks* it's still open but never delivered the
      // background-period events (mobile Safari can do this).
      invalidate();
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        clearReconnectTimer();
        attemptRef.current = 0;
        setStatus("reconnecting");
        connect();
      }
    };

    const handleOnline = () => {
      if (sessionEndedRef.current) return;
      clearReconnectTimer();
      attemptRef.current = 0;
      setStatus("reconnecting");
      connect();
    };

    const handleOffline = () => {
      setStatus("offline");
      clearReconnectTimer();
      closeSocket(wsRef.current);
    };

    document.addEventListener("visibilitychange", handleVisibilityOrFocus);
    window.addEventListener("focus", handleVisibilityOrFocus);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    connect();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
      window.removeEventListener("focus", handleVisibilityOrFocus);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearReconnectTimer();
      // Detach handlers before closing — see closeSocket() doc above.
      // Critical for React Strict Mode dev double-effect; without it
      // the cleanup's close fires onclose → "reconnecting", and the
      // re-mount's open then shows the "Tilkoblet igjen" toast on
      // every fresh page load.
      closeSocket(wsRef.current);
      wsRef.current = null;
    };
  }, [enabled, queryClient, navigate]);

  return { status };
}
